import { BronsonModal, filters, ModalProps } from '@dh/bronson-react';
import React, { useState } from 'react';

import utf8 from 'utf8';
import { hrefPreauthorization } from '../../config';
import { StorefrontModel } from '../../utils/interfaces';
import { CalculatorService } from '../../Views/Calculator/calculator.service';
import { PorscheButton } from '../Button/button';
import { LegalPopUp } from '../Legal/popup';
import { LegalPopUpProps } from '../Legal/popup.type';
import { customClassModifierToTooltip } from './totals.constants';
import { TotalLabels } from './totals.labels';
import { TotalPropsComponent } from './totals.type';

/**
 * This react component build the content for the legal terms
 * and is used in to the modal component.
 * @param props
 */
const ContentTerms: React.FC<{
    dynamicLegalTerms: string;
    hideDefaultLegalTerms?: boolean;
}> = (props) => {
    const legalDecoded = utf8.decode(props.dynamicLegalTerms);
    return (
        <label>
            {!props.hideDefaultLegalTerms && (
                <label>{TotalLabels.tooltipModal.defaultLegalTerms}</label>
            )}
            <label
                dangerouslySetInnerHTML={{ __html: legalDecoded }}
                className='u-mt-small'
            ></label>
        </label>
    );
};

/**
 * This function build the totals component.
 * @param props
 */
export const Totals: React.FC<TotalPropsComponent> = (props) => {
    const calculatorService = new CalculatorService();
    const [hiddenLegalTermsTooltip, setHiddenLegalTermsTooltip] =
        useState(true);
    const [hiddenLegalTermsModal, setHiddenLegalTermsModal] = useState(true);

    /**
     * This function send the data selected to the storefront service,
     * close the legal modal and will be redirect to `hrefPreauthorization`
     * if the response of the service is ok, else it will open the error component.
     */
    const setTheSelectedDataIntoStoreFront = () => {
        preApprovalHandler()
            .then(({ id }) => {
                props.trackingManager.gotoPreApprovalApp('modal-footer-btn');
                setHiddenLegalTermsModal(true);
                const queryString = `?cid=${id}`;
                window.open(
                    `https://${props.brand.toLocaleLowerCase()}.${hrefPreauthorization}${queryString}`,
                    '_blank',
                );
            })
            .catch(() => props.errorOcurred(true));
    };

    const tooltipModalProps: ModalProps = {
        footerCloseButton: false,
        isHidden: hiddenLegalTermsTooltip,
        title: TotalLabels.tooltipModal.title,
        content: (
            <ContentTerms
                dynamicLegalTerms={props.legalTerms}
                hideDefaultLegalTerms={props.hideDefaultLegalTerms}
            />
        ),
        onClose: () => setHiddenLegalTermsTooltip(true),
        customClassModifier: customClassModifierToTooltip,
    };

    const legalModalProps: LegalPopUpProps = {
        brand: props.brand,
        isHidden: hiddenLegalTermsModal,
        onClose: (event) =>
            event
                ? setTheSelectedDataIntoStoreFront()
                : setHiddenLegalTermsModal(true),
    };

    const preApprovalHandler = async (): Promise<StorefrontModel> => {
        const MONTHS = 'MONTHS';
        const storefrontData: StorefrontModel = {
            ...props.externalStoreFront,
            financialProduct: {
                calculation: {
                    ...props.externalStoreFront?.financialProduct?.calculation,
                    duration: props.duration,
                    durationUnit: MONTHS,
                    durationId: props.durationId,
                    monthlyAmount: props.installmentAmount,
                    downPaymentAmount: props.totalHitch,
                    downPaymentPercentage: props.downPaymentPercentage,
                },
                salesCondition: {
                    ...props.externalStoreFront?.financialProduct
                        ?.salesCondition,
                    productId: `${props.productId}`,
                    productName: props.productName,
                },
            },
            vehicleData: {
                ...props.externalStoreFront?.vehicleData,
                brand: props.brand.toLowerCase(),
                modelName: props.model,
                modelId: props.modelId,
                modelYear: +props.year,
                version: props.version,
                versionId: props.versionId,
                purchasePriceAmount: props.retailPrice,
                usedCar: !!props.isUsed,
            },
        };

        return calculatorService.sendStorefrontData(
            props.brand,
            storefrontData,
            !!props.isUsed,
        );
    };

    return (
        <section className='o-content-section u-mt-xlarge'>
            <div className='c-compare-and-select-container'>
                <hr className='c-divider' />
                <div className='o-layout o-layout--middle u-ml-none@s'>
                    <div className='o-layout__item u-10/12 u-9/12@m u-1/1@s u-pl-none@s'>
                        <h6 id='lbl-retail-price' className='u-mb-xxsmall'>
                            {TotalLabels.retailPriceTitle}
                        </h6>

                        <h3 id='val-retail-price' className='u-mt-xxsmall'>
                            <b>{`${filters.currency(props.totalHitch)} ${
                                TotalLabels.mxn
                            }`}</b>
                        </h3>

                        <h5
                            id='lbl-installment-amount'
                            className='u-mb-xxsmall'
                        >
                            {TotalLabels.installmentAmountTitle}
                        </h5>

                        <h2 id='val-installment-amount' className='u-mt-none'>
                            <b>{`${filters.currency(props.installmentAmount)} ${
                                TotalLabels.mxn
                            }`}</b>
                            <button
                                id='btn-legal-terms'
                                type='button'
                                className='u-ml-xsmall js-tooltip c-tooltip c-icon c-icon--[semantic-info] c-icon--medium'
                                onClick={() =>
                                    setHiddenLegalTermsTooltip(false)
                                }
                            ></button>
                        </h2>
                    </div>

                    <div className='o-layout__item u-2/12 u-3/12@m u-1/1@s u-pl-none'>
                        <div className='o-button-container  o-button-container--right'>
                            <PorscheButton
                                id='view-btn'
                                onClick={() => {
                                    props.trackingManager.openModalToOut(
                                        'view-btn',
                                    );
                                    setHiddenLegalTermsModal(false);
                                }}
                                label={
                                    props.brand.toLowerCase() === 'audi' &&
                                    props.isUsed
                                        ? TotalLabels.buttonAudiUsed
                                        : TotalLabels.button
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>

            <BronsonModal {...tooltipModalProps} />
            <LegalPopUp {...legalModalProps} />
        </section>
    );
};
